import React, { useState, useEffect, useMemo } from 'react';
import { database } from "../../firebase";
import { ref, onValue, set, get } from "firebase/database";
import Sidebar from "../Sidebar/SidebarAdmin";
import {
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  Snackbar,
  IconButton,
  Card,
  CircularProgress,
  Tooltip,
  Grid,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Timer as TimerIcon,
  AccessTime as AccessTimeIcon,
  Storage as StorageIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  EventAvailable as EventAvailableIcon
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const PLUS_COLOR = "#14A3C7";
const MINUS_COLOR = "#EC6D71";

const customTheme = createTheme({
  palette: {
    primary: { main: '#14A3C7', contrastText: '#ffffff' },
    secondary: { main: '#EC6D71', contrastText: '#ffffff' },
    error: { main: '#f44336' },
    background: { default: '#F8F8F8' },
    text: { primary: '#333333', secondary: '#666666' }
  },
  typography: { fontFamily: 'Montserrat, sans-serif' }
});

const styles = {
  container: {
    padding: "20px",
    backgroundColor: "#f4f4f4",
    minHeight: "100vh",
    paddingTop: "70px"
  }
};

const Settings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentUser = "Nobunaga-2001"; // Current user's login
  const currentDateTime = "2025-06-09 18:57:07"; // Current UTC time

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isExtendingLockers, setIsExtendingLockers] = useState(false);
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);

  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);
  const sidebarWidth = isSidebarOpen ? 250 : 80;

  const [Reports, setReports] = useState({
    extendPerPeso: 0,
    timePerPeso: 0,
    lockerLength: 0,
    hopperNode: 0,
    violationThreshold: 0,
    warningThreshold: 0
  });
  const [initialReports, setInitialReports] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isEditable, setIsEditable] = useState(false);

  // Firebase references
  const firebaseRefs = useMemo(() => ({
    extendPerPeso: ref(database, "extend_per_peso"),
    timePerPeso: ref(database, "time_per_peso"),
    lockerLength: ref(database, "door/lockerlength"),
    hopperNode: ref(database, "hopper"),
    violationThreshold: ref(database, "expirytimetemplate/violationThreshold"),
    warningThreshold: ref(database, "expirytimetemplate/warningThreshold")
  }), []);

  // Function to extend all locker expiry times by 24 hours
  const handleExtendLockersForHoliday = async () => {
    setIsExtendingLockers(true);
    try {
      // Get current lockers data
      const lockersRef = ref(database, "lockers");
      const lockersSnapshot = await get(lockersRef);
      const lockersData = lockersSnapshot.val();

      if (!lockersData) {
        throw new Error("No lockers data found");
      }

      // Create updated lockers object
      const updatedLockers = {};
      Object.entries(lockersData).forEach(([lockerId, lockerData]) => {
        if (lockerData.availability === "occupied" && lockerData.expiryTime) {
          // Add 24 hours to the expiry time
          const currentExpiry = new Date(lockerData.expiryTime);
          const newExpiry = new Date(currentExpiry.getTime() + (24 * 60 * 60 * 1000));
          
          updatedLockers[lockerId] = {
            ...lockerData,
            expiryTime: newExpiry.toISOString(),
            userHistory: [
              ...(lockerData.userHistory || []),
              {
                addedMinutes: 1440, // 24 hours in minutes
                addedPesos: 0,
                event: "holiday extension",
                fingerprint: lockerData.fingerprintID,
                timestamp: new Date(currentDateTime).toISOString(),
                extendedBy: currentUser
              }
            ]
          };
        } else {
          updatedLockers[lockerId] = lockerData;
        }
      });

      // Update the database
      await set(lockersRef, updatedLockers);

      setSnackbar({
        open: true,
        message: 'Successfully extended all occupied lockers by 24 hours!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error extending lockers:', error);
      setSnackbar({
        open: true,
        message: 'Failed to extend lockers. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsExtendingLockers(false);
      setHolidayDialogOpen(false);
    }
  };

  // Fetch Reports from Firebase
  useEffect(() => {
    const unsubscribes = Object.entries(firebaseRefs).map(([key, refItem]) =>
      onValue(refItem, snapshot => {
        try {
          const value = snapshot.val();
          if (value !== null) {
            setReports(prev => ({ ...prev, [key]: value }));
            setInitialReports(prev => ({ ...prev, [key]: value }));
          }
        } catch (err) {
          console.error(`Error fetching ${key}:`, err);
          setError(`Failed to load ${key} setting`);
        }
      })
    );
    setLoading(false);
    return () => unsubscribes.forEach(unsub => unsub());
  }, [firebaseRefs]);

  // Detect changes
  useEffect(() => {
    const changed = Object.keys(Reports).some(
      key => Reports[key] !== initialReports[key]
    );
    setHasChanges(changed);
  }, [Reports, initialReports]);

  const handleIncrement = field => {
    setReports(prev => ({ ...prev, [field]: prev[field] + 1 }));
  };

  const handleDecrement = field => {
    setReports(prev => ({
      ...prev,
      [field]: Math.max(0, prev[field] - 1)
    }));
  };

  const handleConfirmSave = () => setShowConfirm(true);
  const handleCloseConfirm = () => setShowConfirm(false);

  const handleSave = async () => {
    setShowConfirm(false);
    setLoading(true);
    try {
      await Promise.all(
        Object.entries(Reports).map(([key, value]) =>
          set(firebaseRefs[key], value)
        )
      );
      setInitialReports(Reports);
      setSnackbar({
        open: true,
        message: 'Settings saved successfully!',
        severity: 'success'
      });
      setIsEditable(false);
    } catch (err) {
      console.error('Error saving settings:', err);
      setSnackbar({
        open: true,
        message: 'Failed to save settings. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setReports(initialReports);
    setSnackbar({
      open: true,
      message: 'Changes cancelled',
      severity: 'info'
    });
    setIsEditable(false);
  };

  const getCardBgColor = () => (isEditable ? "#FFFFFF" : "#ECECEC");

  const settingFields = [
    {
      id: 'extendPerPeso',
      label: 'Extend Time Per Peso',
      icon: <TimerIcon fontSize="medium" />,
      tooltip: 'Minutes added per peso for time extension',
      helperText: 'Minutes added per peso when extending time'
    },
    {
      id: 'timePerPeso',
      label: 'Initial Time Per Peso',
      icon: <AccessTimeIcon fontSize="medium" />,
      tooltip: 'Minutes given per peso on first use',
      helperText: 'Minutes given per peso for initial rental'
    },
    {
      id: 'lockerLength',
      label: 'Number of Lockers',
      icon: <StorageIcon fontSize="medium" />,
      tooltip: 'Total number of lockers available',
      helperText: 'Total available locker units'
    },
    {
      id: 'hopperNode',
      label: 'Hopper Node',
      icon: <InfoIcon fontSize="medium" />,
      tooltip: 'Value of the straight-up hopper node',
      helperText: 'Straight-up hopper node value'
    },
    {
      id: 'violationThreshold',
      label: 'Violation Threshold',
      icon: <TimerIcon fontSize="medium" />,
      tooltip: 'Hours before violation threshold',
      helperText: 'Violation threshold in hours'
    },
    {
      id: 'warningThreshold',
      label: 'Warning Threshold',
      icon: <AccessTimeIcon fontSize="medium" />,
      tooltip: 'Hours before warning threshold',
      helperText: 'Warning threshold in hours'
    }
  ];

  if (loading && !Object.keys(Reports).length) {
    return (
      <ThemeProvider theme={customTheme}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            fontFamily: "Montserrat, sans-serif"
          }}
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={customTheme}>
      <div style={{ display: "flex" }}>
        <Sidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

        <Box
          sx={{
            marginLeft: isMobile ? -2 : `${sidebarWidth + 10}px`,
            width: isMobile ? "100%" : `calc(100% - ${sidebarWidth}px)`,
            transition: "margin-left 0.3s ease",
            ...styles.container,
            paddingBottom: isMobile ? "80px" : 0,
            marginTop: isMobile ? "10px" : "-40px",
          }}
        >
          {/* Header with Holiday Extension Button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: "bold", color: "#0099B0" }}>
              System Settings
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<EventAvailableIcon />}
                onClick={() => setHolidayDialogOpen(true)}
                disabled={isExtendingLockers}
              >
                Extend for Holiday
              </Button>
              {!isEditable ? (
                <Button variant="contained" color="primary" onClick={() => setIsEditable(true)}>
                  Edit
                </Button>
              ) : (
                <Button variant="contained" color="secondary" onClick={() => setIsEditable(false)}>
                  Close
                </Button>
              )}
            </Stack>
          </Box>

          {/* Holiday Extension Confirmation Dialog */}
          <Dialog
            open={holidayDialogOpen}
            onClose={() => setHolidayDialogOpen(false)}
            aria-labelledby="holiday-dialog-title"
          >
            <DialogTitle id="holiday-dialog-title">
              Extend Lockers for Holiday
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" gutterBottom>
                This will extend all occupied lockers by 24 hours. Are you sure you want to proceed?
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Current Date and Time: {currentDateTime} UTC
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Administrator: {currentUser}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setHolidayDialogOpen(false)} color="error">
                Cancel
              </Button>
              <Button
                onClick={handleExtendLockersForHoliday}
                color="primary"
                variant="contained"
                disabled={isExtendingLockers}
                startIcon={isExtendingLockers ? <CircularProgress size={20} /> : null}
              >
                {isExtendingLockers ? 'Extending...' : 'Confirm Extension'}
              </Button>
            </DialogActions>
          </Dialog>

          {error && (
            <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
              {error}
            </Alert>
          )}

          {showConfirm && (
            <Snackbar open={showConfirm} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
              <Alert
                severity="info"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  fontFamily: "Montserrat, sans-serif"
                }}
                action={
                  <Stack direction="row" spacing={1}>
                    <Button variant="outlined" color="error" onClick={handleCloseConfirm}>
                      No
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleSave}>
                      Yes
                    </Button>
                  </Stack>
                }
              >
                Are you sure you want to save changes?
              </Alert>
            </Snackbar>
          )}

          {/* Settings Grid */}
          <Grid
            container
            justifyContent="center"
            rowSpacing={1}
            columnSpacing={1}
            sx={{ maxWidth: 900, mx: "auto", mt: 7 }}
          >
            {settingFields.map(field => (
              <Grid
                item
                xs={6}
                sm={6}
                md={4}
                key={field.id}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <Card
                  elevation={3}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    textAlign: "center",
                    width: "100%",
                    maxWidth: 240,
                    minHeight: 300,
                    backgroundColor: getCardBgColor(),
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between"
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="center"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    {field.icon}
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      {field.label}
                    </Typography>
                    <Tooltip title={field.tooltip}>
                      <IconButton size="medium">
                        <InfoIcon fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Stack direction="column" spacing={2} alignItems="center">
                      <IconButton
                        onClick={() => handleIncrement(field.id)}
                        disabled={!isEditable}
                        sx={{
                          backgroundColor: PLUS_COLOR,
                          color: "#fff",
                          "&:hover": { backgroundColor: `${PLUS_COLOR}CC` },
                          width: 48,
                          height: 48
                        }}
                      >
                        <AddIcon sx={{ fontSize: 28 }} />
                      </IconButton>

                      <Typography variant="h3" sx={{ width: 60, textAlign: "center", fontWeight: "bold" }}>
                        {Reports[field.id].toString().padStart(2, "0")}
                      </Typography>

                      <IconButton
                        onClick={() => handleDecrement(field.id)}
                        disabled={!isEditable}
                        sx={{
                          backgroundColor: MINUS_COLOR,
                          color: "#fff",
                          "&:hover": { backgroundColor: `${MINUS_COLOR}CC` },
                          width: 48,
                          height: 48
                        }}
                      >
                        <RemoveIcon sx={{ fontSize: 28 }} />
                      </IconButton>
                    </Stack>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "center", px: 1 }}>
                    {field.helperText}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Save / Cancel bar */}
          {isEditable && (
            <Box
              sx={{
                position: isMobile ? "fixed" : "static",
                bottom: isMobile ? 0 : "auto",
                left: isMobile ? 0 : "auto",
                width: isMobile ? "100%" : "auto",
                backgroundColor: isMobile ? "#fff" : "transparent",
                boxShadow: isMobile ? "0 -2px 8px rgba(0,0,0,0.1)" : "none",
                py: isMobile ? 1 : 0,
                display: "flex",
                justifyContent: "center",
                zIndex: theme.zIndex.appBar
              }}
            >
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={!hasChanges || loading}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleConfirmSave}
                  disabled={!hasChanges || loading}
                >
                  Save
                </Button>
              </Stack>
            </Box>
          )}

          {/* Global Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              variant="filled"
              sx={{
                fontFamily: 'Montserrat, sans-serif',
                borderRadius: 2,
                minWidth: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </div>
    </ThemeProvider>
  );
};

export default Settings;